import { memo, useCallback, useMemo } from 'react';
import { RackingArea } from '@thive/fddm-types';

import { SelectInput } from '@/modules/common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const OPTIONS: { labelKey: string; value: RackingArea['connectNeighbors'] }[] = [
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.no',
    value: 'NO_NEIGHBOR_MAPPING',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.curved_forward_only',
    value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.curved_back_and_forth',
    value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_BACK_AND_FORTH',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.pivot_forward_only',
    value: 'MAP_NEIGHBORS_WITH_PIVOTS_FORWARD_ONLY',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.pivot_back_and_forth',
    value: 'MAP_NEIGHBORS_WITH_PIVOTS_BACK_AND_FORTH',
  },
];

interface Props {
  selectedRackingAreas: RackingArea[];
}

function RackingConnectNeighborsComponent({ selectedRackingAreas }: Props) {
  const optionsOptions = useTranslatedOptions(OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const connectNeighbors = useMemo(
    () => getCommonOption(selectedRackingAreas.map((area) => area.connectNeighbors)),
    [selectedRackingAreas],
  );

  const onChange = useCallback(
    (newValue: RackingArea['connectNeighbors']) => {
      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, { ...area, connectNeighbors: newValue });
      });
      fddmSvc.updateAreas(updatedAreas);
      fddmSvc.setPreference('rackingArea.connectNeighbors', newValue);
    },
    [selectedRackingAreas, fddmSvc],
  );

  return (
    <SelectInput options={optionsOptions} onChange={onChange} value={connectNeighbors} showMixed />
  );
}

export const RackingConnectNeighbors = memo(RackingConnectNeighborsComponent);
